/**
 * Helpers operating on the canonical FareDetailAdditionalService[] and
 * FlightPrice.additionalServices[] arrays — bag/seat/sportsEquipment/
 * service entries committed to a FlightOffer. Pure reads + transforms.
 */

import { apply_markup_to_number } from "@liveflight/utilities/price";
import { traveler_type } from "./travelers";

// Generic ancillary types that share the per-traveler-per-segment
// selection shape — everything except `seat`, which has its own
// per-segment dict.
const GENERIC_ANCILLARY_TYPES = ["bag", "sportsEquipment", "service", "meal", "loungeAccess", "disabilityAssistance"];

/**
 * Project a single ExtraBag-shaped catalog entry into the per-item shape
 * rendered by additional-bags / additional-services blocks (post-markup
 * priceAmount, resolved icon, primary segmentId). Returns `null` when the
 * traveler isn't eligible for this entry.
 */
const project_catalog_item = (
  entry,
  traveler_id,
  flight_offer,
  default_icon,
) => {
  if (!entry) return null;
  const {
    travelerIds = [],
    segmentIds: [segmentId = ""] = [],
    price = {},
    name,
    subtitle,
    icon,
  } = entry;
  if (!travelerIds.includes(String(traveler_id))) return null;
  return {
    segmentId,
    title: name,
    subtitle,
    icon: icon || default_icon,
    priceAmount: apply_markup_to_number(flight_offer, price.total || "0"),
    currencySymbol: price.currencySymbol || "",
  };
};

/**
 * Reshape an ExtraBag-shaped catalog (kyte's bags / sportsEquipment /
 * services slices, or amadeus's flexible bags) into a per-traveler,
 * per-segment grid the additional-bags / additional-services blocks render
 * directly. Each leaf item carries its post-markup `priceAmount` so the
 * renderer doesn't have to know about markup math.
 */
export const group_catalog_by_traveler_and_segment = (
  catalog,
  travelers,
  flight_offer,
  default_icon,
) =>
  (travelers || []).map((traveler) => {
    const items = Object.keys(catalog || {})
      .map((compound_key) => {
        const projected = project_catalog_item(
          (catalog || {})[compound_key],
          traveler.id,
          flight_offer,
          default_icon,
        );
        return projected && { compound_key, ...projected };
      })
      .filter(Boolean)
      .sort(
        (item1, item2) =>
          Number(item1.priceAmount || 0) - Number(item2.priceAmount || 0),
      );

    const grouped = {};
    items.forEach((item) => {
      if (!grouped[item.segmentId]) grouped[item.segmentId] = [];
      grouped[item.segmentId].push(item);
    });

    return {
      ...traveler,
      groups: Object.keys(grouped).map((segmentId) => ({
        segmentId,
        items: grouped[segmentId],
      })),
    };
  });

/**
 * Apply a mutator to one traveler's per-traveler bucket inside a canonical
 * selections object `{ [traveler_id]: { [compound_key]: qty } }`, returning
 * a fresh top-level value suitable for emitting to a v-model `input`
 * listener. Mutator receives the per-traveler bucket clone.
 */
export const update_traveler_selection = (value, traveler_id, mutator) => {
  const next = { ...(value || {}) };
  const per_traveler = { ...(next[traveler_id] || {}) };
  mutator(per_traveler);
  next[traveler_id] = per_traveler;
  return next;
};

/**
 * Italian label for an additional-service line item rendered in the
 * resume sidebar / receipt. Accepts both the legacy uppercase Amadeus
 * vocabulary (CHECKED_BAGS / SEATS / MEALS) and the canonical lowercase
 * types (bag / seat / sportsEquipment / service) emitted by the new
 * providers — callers pass whichever they have.
 * @param {string} service_type
 * @param {number} [service_quantity=1]
 * @returns {string}
 */
export function transcode_additional_services(
  service_type,
  service_quantity = 1,
) {
  const plural = service_quantity > 1;
  switch (service_type) {
    case "CHECKED_BAGS":
    case "bag":
      return plural ? "bagagli extra in stiva" : "bagaglio extra in stiva";
    case "MEALS":
    case "meal":
      return plural ? "pasti" : "pasto";
    case "SEATS":
    case "seat":
      return plural ? "posti a sedere" : "posto a sedere";
    case "SPORTS_EQUIPMENT":
    case "sportsEquipment":
      return plural ? "attrezzature sportive" : "attrezzatura sportiva";
    case "SERVICES":
    case "service":
      return plural ? "servizi extra" : "servizio extra";
    case "LOUNGE_ACCESS":
    case "loungeAccess":
      return "accesso lounge";
    case "DISABILITY_ASSISTANCE":
    case "disabilityAssistance":
      return "assistenza speciale";
    default:
      return "altri servizi";
  }
}

/**
 * Tooltip copy for the additional-service line item, by service type.
 * @param {string} service_type
 * @returns {string}
 */
export function get_additional_service_tooltip(service_type) {
  switch (service_type) {
    case "CHECKED_BAGS":
    case "bag":
      return "Sono i bagagli aggiunti e non inclusi nella tariffa iniziale.";
    default:
      return "";
  }
}

/**
 * FlightPrice.additionalServices entries with a non-negative price —
 * committed paid extras, vs roll-up entries that carry only a type.
 * Defensive: returns [] on any missing piece.
 * @param {object} flight_offer
 * @returns {Array<object>}
 */
export const get_paid_additional_services = (flight_offer) => {
  const { price: { additionalServices = [] } = {} } = flight_offer || {};
  if (!Array.isArray(additionalServices)) return [];
  return additionalServices.filter((addition) => {
    const { price = {} } = addition || {};
    const amount = price.total || price.grandTotal;
    return Number(amount || "-1") >= 0;
  });
};

/**
 * Wipe every committed additional-service mark off a canonical
 * FlightOffer and collapse `price.grandTotal` back to the fare-only
 * `price.total`. Avoids double-counting committed ancillaries against
 * the page-side `uncommitted_additions` re-seeded by
 * build_committed_selections. Clears:
 *   1. `price.additionalServices` (offer-level roll-up)
 *   2. `travelerPricings[].fareDetailsBySegment[].additionalServices`
 *      (per-(pax, segment) entries)
 *   3. `price.grandTotal` ← `price.total` (no-op at search time, where
 *      they're equal).
 * @param {object} flight_offer
 * @returns {object}
 */
export const strip_offer_additional_services = (flight_offer) => {
  if (!flight_offer) return flight_offer;
  const { price = {}, travelerPricings = [] } = flight_offer;
  const fare_only_total = price.total || price.grandTotal || "0";
  return {
    ...flight_offer,
    price: {
      ...price,
      grandTotal: fare_only_total,
      additionalServices: [],
    },
    travelerPricings: travelerPricings.map((pricing) => {
      const { fareDetailsBySegment = [] } = pricing || {};
      return {
        ...pricing,
        fareDetailsBySegment: fareDetailsBySegment.map((fare_detail) => ({
          ...(fare_detail || {}),
          additionalServices: [],
        })),
      };
    }),
  };
};

/**
 * Drop catalog entries already bundled with the chosen fare. Caller
 * passes the SUBSET of committed ancillaries flagged `included: true`;
 * catalog entries whose canonical `id` matches an included entry of the
 * same type are dropped (a Plus fare bundling 2× 20kg bags should not
 * also offer BBG paid). Seats are skipped — the picker handles those.
 * Slices that end up empty are hidden by `steps()` downstream.
 * @param {object} envelope Catalog envelope keyed by canonical type
 * @param {Array<{type: string, id: string}>} included_ancillaries
 * @returns {object}
 */
export const filter_catalog_by_included = (envelope, included_ancillaries) => {
  const safe = envelope || {};
  const list = Array.isArray(included_ancillaries) ? included_ancillaries : [];
  const skip_by_type = {};
  list.forEach((entry) => {
    const { type = "", id = "" } = entry || {};
    if (!type || !id) return;
    if (!skip_by_type[type]) skip_by_type[type] = new Set();
    skip_by_type[type].add(id);
  });
  if (!Object.keys(skip_by_type).length) return safe;
  const next = { ...safe };
  GENERIC_ANCILLARY_TYPES.forEach((type) => {
    const skip = skip_by_type[type];
    if (!skip || !skip.size) return;
    const slice = safe[type];
    if (!slice || typeof slice !== "object") return;
    const filtered = {};
    Object.keys(slice).forEach((compound_key) => {
      const item = slice[compound_key];
      const { id: item_id = "" } = item || {};
      if (item_id && skip.has(item_id)) return;
      filtered[compound_key] = item;
    });
    next[type] = filtered;
  });
  return next;
};

/**
 * True when a seatmap envelope cannot service every itinerary segment.
 *   - `empty` / no decks → upstream returned nothing useful.
 *   - any `mappedDecks` segment flagged `disabled` → would render as
 *     un-pickable, blocking fares that mandate a per-segment pick.
 * Only invoked when seats_are_mandatory is true; optional offers fall
 * through and let the user skip the seat step.
 * @param {Object} seatmap
 * @returns {boolean}
 */
export const seatmap_blocks_progress = (seatmap) => {
  const { empty = false, mappedDecks = {} } = seatmap || {};
  if (!seatmap || empty) return true;
  const entries = Object.values(mappedDecks);
  if (!entries.length) return true;
  return entries.some((entry) => {
    const { segment = {} } = entry || {};
    return segment.disabled === true;
  });
};

/**
 * Derive the wizard-facing traveler list from a canonical FlightOffer
 * (for type) and the form's `traveler_details` (for name + DOB +
 * gender), keyed by travelerId. Output drives the per-pax columns in
 * additional-seats / additional-ancillaries.
 * @param {object} flight_offer
 * @param {Array<object>} traveler_details
 * @returns {Array<{id: string, type: string, firstname: string, middlename: string, lastname: string, birth: string, gender: string}>}
 */
export const derive_travelers = (flight_offer, traveler_details) => {
  const { travelerPricings = [] } = flight_offer || {};
  const details = Array.isArray(traveler_details) ? traveler_details : [];
  const detail_by_id = details.reduce((acc, detail) => {
    const safe = detail || {};
    if (safe.id) acc[safe.id] = safe;
    return acc;
  }, {});
  return travelerPricings.map((pricing) => {
    const { travelerId = ""} = pricing || {};
    const {
      firstname = "",
      middlename = "",
      lastname = "",
      birth = "",
      gender = "",
    } = detail_by_id[travelerId] || {};
    return {
      id: travelerId,
      type: traveler_type(pricing),
      firstname,
      middlename,
      lastname,
      birth,
      gender,
    };
  });
};

/**
 * Build the resume-sidebar entry for the seat step from the canonical
 * `selections.seat` shape `{ [segmentId]: { [travelerId]: SeatEntry } }`.
 * Raw `total` only — the resume applies markup at render; using
 * `grandTotal` (already marked-up on per-seat blocks) would double-apply.
 * @param {object} seat_selections
 * @returns {{type: 'SEATS', amount: number, info: object} | null}
 */
export const build_uncommitted_seat_entry = (seat_selections) => {
  const safe = seat_selections || {};
  const info = {};
  let amount = 0;
  Object.keys(safe).forEach((segment_id) => {
    const per_pax = safe[segment_id] || {};
    Object.keys(per_pax).forEach((traveler_id) => {
      const seat_entry = per_pax[traveler_id] || {};
      const {
        chargeableSeatNumber,
        number,
        seatNumber,
        pricingByTraveler = {},
        price: entry_price = {},
      } = seat_entry;
      const resolved_seat_number =
        seatNumber || chargeableSeatNumber || number;
      if (!resolved_seat_number) return;
      const { price: pax_price = {}, traveler_name = "" } =
        pricingByTraveler[traveler_id] || {};
      const unit_amount =
        Number(pax_price.total) || Number(entry_price.total) || 0;
      if (!info[segment_id]) info[segment_id] = [];
      info[segment_id].push({
        seatNumber: resolved_seat_number,
        travelerId: traveler_id,
        traveler_name,
      });
      amount += unit_amount;
    });
  });
  if (!Object.keys(info).length) return null;
  return { type: "SEATS", amount, info };
};

/**
 * Build the resume-sidebar entry for a non-seat step (bag /
 * sportsEquipment / service) from the per-step selection slice and the
 * matching catalog (`ancillaries[step_id]`). `step_selections` shape:
 * `{ [travelerId]: { [compound_key]: quantity } }`. Raw `total` only —
 * markup is applied at render in resume.vue.
 * @param {object} step_selections
 * @param {string} type Canonical AdditionalService type (CHECKED_BAGS, …)
 * @param {object} catalog Catalog slice keyed by compound_key
 * @returns {{type: string, amount: number, info: object} | null}
 */
export const build_uncommitted_step_entry = (
  step_selections,
  type,
  catalog,
) => {
  const per_traveler = step_selections || {};
  const safe_catalog = catalog || {};
  const info = {};
  let amount = 0;
  Object.keys(per_traveler).forEach((traveler_id) => {
    const picks = per_traveler[traveler_id] || {};
    Object.keys(picks).forEach((compound_key) => {
      const quantity = Number(picks[compound_key]) || 0;
      if (quantity <= 0) return;
      const entry = safe_catalog[compound_key];
      if (!entry) return;
      const description = entry.name || entry.id || compound_key;
      const { price = {} } = entry;
      const unit_amount = Number(price.total) || 0;
      if (!info[compound_key]) {
        info[compound_key] = {
          description,
          icon: entry.icon || "luggage",
          content: 0,
        };
      }
      info[compound_key].content += quantity;
      amount += unit_amount * quantity;
    });
  });
  if (!Object.keys(info).length) return null;
  return { type, amount, info };
};

/**
 * Project the canonical `selections` map into the flat array shape
 * BookAncillaries / compute_ancillary_diff consume:
 * `{ travelerId, type, id, quantity, flightSegments }[]`. Bag-shaped
 * picks split their compound_key on ":" to recover `(item_id, segment_id)`;
 * seats walk the per-segment per-pax dict.
 * @param {{bag?: object, seat?: object, sportsEquipment?: object, service?: object}} selections
 * @returns {Array<{travelerId: string, type: string, id: string, quantity: number, flightSegments: string[]}>}
 */
export const flatten_selections = (selections) => {
  const safe = selections || {};
  const out = [];
  GENERIC_ANCILLARY_TYPES.forEach((type) => {
    const per_traveler = safe[type] || {};
    Object.keys(per_traveler).forEach((traveler_id) => {
      const picks = per_traveler[traveler_id] || {};
      Object.keys(picks).forEach((compound_key) => {
        const quantity = Number(picks[compound_key]) || 0;
        if (quantity <= 0) return;
        // Split from the right: ids can contain colons (Kyte format),
        // segment id is always the trailing token.
        const split_at = compound_key.lastIndexOf(":");
        const item_id = split_at >= 0 ? compound_key.slice(0, split_at) : compound_key;
        const segment_id = split_at >= 0 ? compound_key.slice(split_at + 1) : "";
        out.push({
          travelerId: traveler_id,
          type,
          id: item_id,
          quantity,
          flightSegments: segment_id ? [segment_id] : [],
        });
      });
    });
  });
  const seat_selections = safe.seat || {};
  Object.keys(seat_selections).forEach((segment_id) => {
    const per_pax = seat_selections[segment_id] || {};
    Object.keys(per_pax).forEach((traveler_id) => {
      const { seatNumber, chargeableSeatNumber, number } =
        per_pax[traveler_id] || {};
      const seat_id = seatNumber || chargeableSeatNumber || number;
      if (!seat_id) return;
      out.push({
        travelerId: traveler_id,
        type: "seat",
        id: seat_id,
        quantity: 1,
        flightSegments: [segment_id],
      });
    });
  });
  return out;
};

/**
 * Diff current selections against state captured at retrieve.
 * Selections not in committed → `to_add`; committed not in selections
 * → `to_remove`. Both lists carry the canonical shape
 * `{ travelerId, type, id, quantity, flightSegments }`.
 *
 * `included: true` entries (fare-bundle freebies — Kyte lists them with
 * no `pricingBreakdown` row) MUST stay in `committed_set` so a matching
 * selection isn't a duplicate `add`, but MUST NOT contribute to
 * `to_remove` — BookAncillaries rejects removals of fare-included
 * entries.
 * @param {Array<object>} committed
 * @param {object} selections
 * @returns {{to_add: Array<object>, to_remove: Array<object>}}
 */
export const compute_ancillary_diff = (committed, selections) => {
  const fingerprint = (entry) => {
    const segs = (entry.flightSegments || []).slice().sort().join(",");
    const qty = Number(entry.quantity) || 1;
    return `${entry.travelerId}|${entry.type}|${entry.id}|${segs}|${qty}`;
  };
  const committed_list = Array.isArray(committed) ? committed : [];
  const selected = flatten_selections(selections);
  const committed_set = new Set(committed_list.map(fingerprint));
  const selected_set = new Set(selected.map(fingerprint));
  return {
    to_add: selected.filter(
      (entry) => !committed_set.has(fingerprint(entry)),
    ),
    to_remove: committed_list
      .filter((entry) => !entry.included)
      .filter((entry) => !selected_set.has(fingerprint(entry))),
  };
};

/**
 * Reconstruct the page-side `selections` map from a canonical FlightOffer.
 * Output keys match the canonical type strings the providers produce.
 * Bag-shaped buckets roll up by quantity per (pax, segment, ancillary id);
 * seats are keyed by (segment, pax) and carry the canonical entry as-is.
 *
 *     {
 *       bag:             { [travelerId]: { '${id}:${segId}': qty } },
 *       seat:            { [segId]: { [travelerId]: SeatEntry } },
 *       sportsEquipment: { [travelerId]: { '${id}:${segId}': qty } },
 *       service:         { [travelerId]: { '${id}:${segId}': qty } },
 *     }
 * @param {object} flight_offer
 * @returns {{bag: object, seat: object, sportsEquipment: object, service: object}}
 */
export const build_committed_selections = (flight_offer) => {
  const out = { bag: {}, seat: {}, sportsEquipment: {}, service: {}, meal: {}, loungeAccess: {}, disabilityAssistance: {} };
  const { travelerPricings = [] } = flight_offer || {};
  travelerPricings.forEach((pricing) => {
    const { travelerId = "", fareDetailsBySegment = [] } = pricing || {};
    if (!travelerId) return;
    fareDetailsBySegment.forEach((fare_detail) => {
      const { segmentId = "", additionalServices = [] } = fare_detail || {};
      if (!segmentId) return;
      const list = Array.isArray(additionalServices) ? additionalServices : [];
      list.forEach((entry) => {
        const {
          type = "",
          id = "",
          quantity = 1,
          seatNumber = "",
        } = entry || {};
        if (out[type] === undefined) return;
        if (type === "seat") {
          if (!seatNumber) return;
          if (!out.seat[segmentId]) out.seat[segmentId] = {};
          out.seat[segmentId][travelerId] = { ...entry, number: seatNumber };
          return;
        }
        if (!id) return;
        const compound_key = `${id}:${segmentId}`;
        if (!out[type][travelerId]) out[type][travelerId] = {};
        out[type][travelerId][compound_key] =
          (out[type][travelerId][compound_key] || 0) + (Number(quantity) || 1);
      });
    });
  });
  return out;
};
